import { computed, ref, watch } from 'vue'

/**
 * Talks to the existing ComponentsController#index JSON (DataTables contract),
 * or filters boot.rows client-side for static surfaces.
 */
export function useLmsIndex (boot) {
  const rows = ref([])
  const total = ref(0)
  const filtered = ref(0)
  const loading = ref(false)
  const error = ref('')
  const pillCounts = ref(boot.pills?.counts || null)
  const activePill = ref(boot.pills?.active || 'All')
  const globalSearch = ref('')
  const options = ref({ active: boot.archived ? 'false' : 'true' })
  const columnFilters = ref({})
  const page = ref(1)
  const itemsPerPage = ref(
    boot.noPaginate ? -1 : (boot.defaultLength || 10)
  )
  const sortBy = ref([
    {
      key: boot.defaultSort?.key || boot.columns?.[0]?.key || 'id',
      order: boot.defaultSort?.dir === 'asc' ? 'asc' : 'desc'
    }
  ])
  const columnVisibility = ref(loadColumnVisibility(boot))
  const selectedIds = ref([])

  let draw = 0
  let debounceTimer = 0

  const visibleColumns = computed(() =>
    (boot.columns || []).filter((c) => columnVisibility.value[c.key] !== false)
  )

  const storageKey = `lms-index:${boot.airport || 'x'}:${boot.type}`

  function loadColumnVisibility (b) {
    try {
      const raw = localStorage.getItem(`lms-index:${b.airport || 'x'}:${b.type}:cols`)
      if (raw) return JSON.parse(raw)
    } catch (_) { /* ignore */ }
    const vis = {}
    ;(b.columns || []).forEach((c) => { vis[c.key] = c.visible !== false })
    return vis
  }

  function persistColumns () {
    try {
      localStorage.setItem(`${storageKey}:cols`, JSON.stringify(columnVisibility.value))
    } catch (_) { /* ignore */ }
  }

  function sortValue (row, key) {
    const explicit = row[`_sort_${key}`]
    if (explicit != null && explicit !== '') return explicit
    const raw = row[key]
    if (raw && typeof raw === 'object') {
      if (raw.timestamp != null) return raw.timestamp
      if (raw.original != null) return raw.original
      if (raw.display != null) return raw.display
    }
    return raw
  }

  function cellText (row, key) {
    const raw = row[key]
    if (raw == null) return ''
    if (typeof raw === 'object') {
      return String(raw.display ?? raw.text ?? raw.original ?? '').replace(/<[^>]+>/g, '')
    }
    return String(raw).replace(/<[^>]+>/g, '')
  }

  function filterStaticList (source) {
    const cols = boot.columns || []
    const q = (globalSearch.value || '').toLowerCase().trim()
    let list = [...(source || [])]

    if (boot.pills && activePill.value && activePill.value !== 'All') {
      const pill = String(activePill.value).toLowerCase()
      const field = boot.pills.field || 'status'
      list = list.filter((row) => String(row[field] || row.status || '').toLowerCase() === pill)
    }

    if (q) {
      list = list.filter((row) =>
        cols.some((c) => cellText(row, c.key).toLowerCase().includes(q))
      )
    }

    Object.entries(columnFilters.value || {}).forEach(([key, val]) => {
      const needle = String(val || '').toLowerCase().trim()
      if (!needle) return
      list = list.filter((row) => cellText(row, key).toLowerCase().includes(needle))
    })

    const sort = sortBy.value?.[0]
    if (sort?.key) {
      const dir = sort.order === 'asc' ? 1 : -1
      list.sort((a, b) => {
        const av = sortValue(a, sort.key)
        const bv = sortValue(b, sort.key)
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        if (av < bv) return -1 * dir
        if (av > bv) return 1 * dir
        return 0
      })
    }
    return list
  }

  function buildParams () {
    const params = new URLSearchParams()
    draw += 1
    params.set('draw', String(draw))

    const length = itemsPerPage.value === -1 ? -1 : itemsPerPage.value
    const start = length === -1 ? 0 : (page.value - 1) * length
    params.set('start', String(start))
    params.set('length', String(length))

    params.set('search[value]', globalSearch.value || '')
    params.set('options[active]', options.value.active || 'true')

    Object.entries(options.value).forEach(([key, val]) => {
      if (key === 'active') return
      if (val == null || val === '') return
      if (typeof val === 'object' && !Array.isArray(val)) {
        Object.entries(val).forEach(([k, v]) => {
          if (v != null && v !== '') params.set(`options[${key}][${k}]`, String(v))
        })
      } else if (Array.isArray(val)) {
        val.forEach((v) => params.append(`options[${key}][]`, String(v)))
      } else {
        params.set(`options[${key}]`, String(val))
      }
    })

    if (boot.pills) {
      params.set('pill', activePill.value || 'All')
    }

    const cols = boot.columns || []
    cols.forEach((col, i) => {
      params.set(`columns[${i}][name]`, col.key)
      params.set(`columns[${i}][data]`, col.key)
      params.set(`columns[${i}][searchable]`, col.searchable === false ? 'false' : 'true')
      params.set(`columns[${i}][orderable]`, col.sortable === false ? 'false' : 'true')
      params.set(`columns[${i}][search][value]`, columnFilters.value[col.key] || '')
    })

    const sort = sortBy.value?.[0]
    if (sort?.key) {
      const idx = cols.findIndex((c) => c.key === sort.key)
      params.set('order[0][column]', String(idx >= 0 ? idx : 0))
      params.set('order[0][dir]', sort.order === 'asc' ? 'asc' : 'desc')
    }

    return params
  }

  async function fetchRows () {
    if (boot.surface === 'static' && Array.isArray(boot.rows)) {
      applyStaticRows()
      return
    }

    loading.value = true
    error.value = ''
    try {
      const url = `${boot.indexUrl}.json?${buildParams().toString()}`
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
      if (!res.ok) throw new Error(`Could not load records (${res.status})`)
      const json = await res.json()
      rows.value = Array.isArray(json.data) ? json.data : []
      total.value = Number(json.total) || 0
      filtered.value = Number(json.num_filtered) || 0
      if (json.index_pill_counts) {
        pillCounts.value = json.index_pill_counts
      }
    } catch (e) {
      error.value = e.message || 'Unable to load records'
      rows.value = []
    } finally {
      loading.value = false
    }
  }

  function applyStaticRows () {
    loading.value = true
    error.value = ''
    try {
      const list = filterStaticList(boot.rows || [])
      if (boot.pills?.counts) {
        const counts = { ...(boot.pills.counts || {}) }
        const all = boot.rows || []
        Object.keys(counts).forEach((status) => {
          if (status === 'All') {
            counts[status] = all.length
          } else {
            counts[status] = all.filter((r) => String(r.status || '') === status).length
          }
        })
        pillCounts.value = counts
      }
      total.value = (boot.rows || []).length
      filtered.value = list.length
      const length = itemsPerPage.value === -1 ? list.length : itemsPerPage.value
      const start = length <= 0 ? 0 : (page.value - 1) * length
      rows.value = length <= 0 ? list : list.slice(start, start + length)
    } finally {
      loading.value = false
    }
  }

  async function fetchAllFilteredRows () {
    if (boot.surface === 'static' && Array.isArray(boot.rows)) {
      return filterStaticList(boot.rows || [])
    }
    const params = buildParams()
    params.set('start', '0')
    params.set('length', '-1')
    const url = `${boot.indexUrl}.json?${params.toString()}`
    const res = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
    if (!res.ok) throw new Error(`Could not load export rows (${res.status})`)
    const json = await res.json()
    return Array.isArray(json.data) ? json.data : []
  }

  function allFilteredIds () {
    if (boot.surface === 'static' && Array.isArray(boot.rows)) {
      return filterStaticList(boot.rows || []).map((r) => r.id).filter((id) => id != null)
    }
    return rows.value.map((r) => r.id).filter((id) => id != null)
  }

  function scheduleFetch (immediate = false) {
    clearTimeout(debounceTimer)
    if (immediate) {
      fetchRows()
      return
    }
    debounceTimer = window.setTimeout(() => fetchRows(), 320)
  }

  function setSearch (value) {
    globalSearch.value = value
    page.value = 1
    scheduleFetch()
  }

  function setColumnFilter (key, value) {
    columnFilters.value = { ...columnFilters.value, [key]: value }
    page.value = 1
    scheduleFetch()
  }

  function setColumnFilters (next) {
    columnFilters.value = { ...(next || {}) }
    page.value = 1
    // Debounced apply comes from IndexColumnFilters @apply
  }

  function setOption (name, value) {
    options.value = { ...options.value, [name]: value }
    page.value = 1
    scheduleFetch(true)
  }

  function applyOptions (nextOptions = {}) {
    const active = options.value.active || (boot.archived ? 'false' : 'true')
    options.value = {
      active,
      ...(nextOptions || {})
    }
    page.value = 1
    scheduleFetch(true)
  }

  function clearOption (name) {
    const next = { ...options.value }
    delete next[name]
    options.value = next
    page.value = 1
    scheduleFetch(true)
  }

  function clearFilters () {
    options.value = { active: boot.archived ? 'false' : 'true' }
    globalSearch.value = ''
    columnFilters.value = {}
    if (boot.pills) activePill.value = boot.pills.active || 'All'
    page.value = 1
    scheduleFetch(true)
  }

  function resetAll () {
    clearFilters()
    sortBy.value = [
      {
        key: boot.defaultSort?.key || boot.columns?.[0]?.key || 'id',
        order: boot.defaultSort?.dir === 'asc' ? 'asc' : 'desc'
      }
    ]
    itemsPerPage.value = boot.noPaginate ? -1 : (boot.defaultLength || 10)
    columnVisibility.value = loadColumnVisibility(boot)
    selectedIds.value = []
    persistColumns()
  }

  function setPill (name) {
    activePill.value = name
    page.value = 1
    scheduleFetch(true)
  }

  function toggleColumn (key) {
    columnVisibility.value = {
      ...columnVisibility.value,
      [key]: columnVisibility.value[key] === false
    }
    persistColumns()
  }

  function rowHref (row) {
    if (row?.path) return row.path
    if (row?.href) return row.href
    if (row?.id == null) return null
    if (boot.rowHrefTemplate) {
      return boot.rowHrefTemplate.replace(':id', String(row.id))
    }
    if (!boot.showBaseUrl) return null
    return `${boot.showBaseUrl}${row.id}`
  }

  function clearSelected () {
    selectedIds.value = []
  }

  function toggleSelected (id) {
    const sid = String(id)
    const set = new Set(selectedIds.value.map(String))
    if (set.has(sid)) set.delete(sid)
    else set.add(sid)
    selectedIds.value = [...set]
  }

  function setSelectedAll (ids, checked) {
    if (!checked) {
      const drop = new Set((ids || []).map(String))
      selectedIds.value = selectedIds.value.filter((id) => !drop.has(String(id)))
      return
    }
    const set = new Set(selectedIds.value.map(String))
    ;(ids || []).forEach((id) => set.add(String(id)))
    selectedIds.value = [...set]
  }

  watch([page, itemsPerPage, sortBy], () => scheduleFetch(true))
  watch(activePill, () => {
    if (boot.surface === 'static') scheduleFetch(true)
  })

  return {
    rows,
    total,
    filtered,
    loading,
    error,
    pillCounts,
    activePill,
    globalSearch,
    options,
    columnFilters,
    page,
    itemsPerPage,
    sortBy,
    columnVisibility,
    visibleColumns,
    selectedIds,
    fetchRows,
    fetchAllFilteredRows,
    allFilteredIds,
    scheduleFetch,
    setSearch,
    setColumnFilter,
    setColumnFilters,
    setOption,
    applyOptions,
    clearOption,
    clearFilters,
    resetAll,
    setPill,
    toggleColumn,
    persistColumns,
    rowHref,
    toggleSelected,
    clearSelected,
    setSelectedAll
  }
}
